import {
  type GetSessionsAssetsResponse,
  useLazyGetSessionsAssetsQuery,
} from '@carboncopyco/data';
import { RightOutlineIcon } from '@carboncopyco/icons';
import { Button, TextLargeParagraph } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
  useDashboardAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useEffect } from 'react';
import { FixedTable } from '../../../../FixedTable';
import { TitleCard } from '../../../../InfoPage/InfoPageCards';
import { AssetTableSkeleton } from '../../../AssetTableSkeleton';
import { useDashboardProps } from '../../../DashboardContext';
import { RowWrapper } from '../../../RowWrapper';
import {
  TableEmptyState,
  TableErrorState,
} from '../../../SessionBreakdownComponents';
import { useAssetStatus } from '../../../hooks';
import { ViewAllDemosModal } from './ViewAllDemosModal';
import { getCols } from './cols';

const cols = getCols();

export const DemosTable = () => {
  const {
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    goToDemo,
    goToPlaybook,
  } = useDashboardProps();
  const [getSessionAssets, { data, isLoading, error }] =
    useLazyGetSessionsAssetsQuery();
  const [showModal, setShowModal] = useBoolean();
  const { trackDashboardViewMoreClick } = useDashboardAnalytics();
  const doesAssetsWorkspaceExists = useAssetStatus();

  useEffect(() => {
    getSessionAssets({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes: ['managed_presentation', 'shared_presentation', 'play'],
    });
  }, [
    organizationId,
    timeRange,
    short,
    internal,
    getSessionAssets,
    selectedWorkspaceIds,
  ]);

  const items = data?.items ?? [];

  const onRowClick = (item: GetSessionsAssetsResponse) => {
    if (item.asset.isDeleted || !doesAssetsWorkspaceExists(item.asset)) {
      return;
    }
    const navFunction = {
      playbook: goToPlaybook,
      demo: goToDemo,
    };
    navFunction[item.asset.assetType](
      item.asset.assetId,
      item.asset.workspaceId
    );
  };

  return (
    <>
      <TitleCard
        border="ds-regular"
        header={
          <div className="pl-6 h-ds-15 flex items-center gap-2">
            <TextLargeParagraph
              label="Demos"
              color="text-ds-natural-600"
            />
            {!!data?.metadata.totalItems && (
              <TextLargeParagraph
                label={`(${data.metadata.totalItems})`}
                color="text-ds-natural-300"
              />
            )}
          </div>
        }
      >
        {error ? (
          <div className="p-28">
            <TableErrorState />
          </div>
        ) : items.length || isLoading ? (
          <div className="flex flex-col pt-4.5 h-full">
            <FixedTable<GetSessionsAssetsResponse>
              sideSpacing
              cols={cols}
              data={data?.items.slice(0, 3) ?? []}
              isLoading={isLoading}
              loader={<AssetTableSkeleton />}
              rowWrapper={({ children, item }) => {
                const separator = item !== items[items.length - 1];
                return (
                  <RowWrapper
                    onRowClick={() => onRowClick(item)}
                    size="lg"
                    separator={separator}
                  >
                    {children}
                  </RowWrapper>
                );
              }}
              loaderPlaceholderSpace={0}
            />
            {!isLoading && items.length > 3 && (
              <div className="flex items-center pl-6 py-4 h-full [&>button]:p-0">
                <Button
                  blue
                  link
                  label="View all"
                  rightIcon={<RightOutlineIcon />}
                  onClick={() => {
                    setShowModal.setTrue();
                    trackDashboardViewMoreClick({
                      dashboardSection: 'presenters_table',
                    });
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 pt-28">
            <TableEmptyState />
          </div>
        )}
      </TitleCard>
      {showModal && (
        <ViewAllDemosModal
          open={showModal}
          onClose={setShowModal.setFalse}
        />
      )}
    </>
  );
};
